// Bảng nhập giá: hàng = một mức cân ("dưới X kg" · "đúng X kg" · "đến X kg") + một hàng "mỗi
// … kg tiếp theo", cột = 4 vùng. Mỗi bảng tự có hàng của mình (thêm / xoá / sửa hàng ngay trong
// bảng khi có onRowsChange); bảng cũ chưa có hàng riêng hiện theo khung chung (toàn "đến X").
// Luật hàng khớp packages/common/src/price-grid.ts (validateGrid / priceFor).

import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { MoneyInput } from '@/components/ui/money-input'
import { useViewportMode } from '@/hooks/useViewportMode'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'

export type ZoneKey = 'noi_tinh' | 'lien_tinh' | 'noi_mien' | 'lien_mien'

export const ZONES: ReadonlyArray<{ key: ZoneKey; label: string }> = [
  { key: 'noi_tinh', label: 'Nội tỉnh' },
  { key: 'lien_tinh', label: 'Liên tỉnh' },
  { key: 'noi_mien', label: 'Nội miền' },
  { key: 'lien_mien', label: 'Liên miền' },
]

/** Loại hàng: dưới X (lt) · đúng X (eq) · đến X, gồm X (le). */
export type RowKind = 'lt' | 'eq' | 'le'
export const ROW_KIND_LABEL: Record<RowKind, string> = { lt: 'Dưới', eq: 'Đúng', le: 'Đến' }

export interface PriceGrid {
  version: number
  /** Mức cân (kg) của từng hàng. */
  breakpointsKg: number[]
  /** Loại từng hàng; thiếu = mọi hàng "đến X" (khung cũ). */
  kinds?: RowKind[]
  stepKg: number
  maxWeightKg: number
}

export interface ZonePrices {
  /** Một giá (VND nguyên) cho mỗi hàng, không giảm theo cân. */
  prices: number[]
  stepPrice: number
}

export type PriceListData = Record<ZoneKey, ZonePrices>

/** Một ô: chỉ số hàng hoặc 'step' (giá mỗi bước trên hàng cuối). */
export interface CellRef {
  zone: string
  cell: number | 'step'
}

const KG = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 3 })
const toGram = (kg: number) => Math.round(kg * 1000)

/** 0.5 → "0,5" */
export function fmtKg(kg: number): string {
  return KG.format(kg)
}

export function parseKg(s: string): number {
  const n = Number(String(s).trim().replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

export const kindAt = (grid: PriceGrid, i: number): RowKind => grid.kinds?.[i] ?? 'le'

export function breakpointLabel(kg: number, kind: RowKind = 'le'): string {
  return `${kind === 'lt' ? '<' : kind === 'eq' ? '=' : '≤'} ${fmtKg(kg)} kg`
}

/** Nhãn ngắn của hàng i: "< 1 kg", "= 1 kg", "≤ 3 kg". */
export function rowLabel(grid: PriceGrid, i: number): string {
  return breakpointLabel(grid.breakpointsKg[i] ?? 0, kindAt(grid, i))
}

/** Khoảng cân thật của hàng i, đọc được: "dưới 1 kg", "đúng 1 kg", "trên 1 – dưới 2 kg", "từ 2 – đến 3 kg". */
export function rowRange(grid: PriceGrid, i: number): string {
  const kg = grid.breakpointsKg[i] ?? 0
  const k = kindAt(grid, i)
  if (k === 'eq') return `đúng ${fmtKg(kg)} kg`
  const upper = `${k === 'lt' ? 'dưới' : 'đến'} ${fmtKg(kg)} kg`
  if (i === 0) return upper
  const p = grid.breakpointsKg[i - 1]!
  return `${kindAt(grid, i - 1) === 'lt' ? 'từ' : 'trên'} ${fmtKg(p)} – ${upper}`
}

export function stepRowLabel(grid: PriceGrid): string {
  return `Mỗi ${fmtKg(grid.stepKg)} kg tiếp theo (tối đa ${fmtKg(grid.maxWeightKg)} kg)`
}

/** Có vùng tính theo bước (cân tối đa lớn hơn hàng cuối) hay không. */
export function hasStepZone(grid: PriceGrid): boolean {
  const last = grid.breakpointsKg[grid.breakpointsKg.length - 1] ?? 0
  return grid.maxWeightKg > last
}

export function emptyPriceListData(grid: PriceGrid): PriceListData {
  const zone = (): ZonePrices => ({ prices: grid.breakpointsKg.map(() => 0), stepPrice: 0 })
  return { noi_tinh: zone(), lien_tinh: zone(), noi_mien: zone(), lien_mien: zone() }
}

/** Đưa dữ liệu về đúng số hàng (thiếu → 0, thừa → bỏ). Bảng cũ chưa có cột Liên tỉnh → lấy theo Nội miền (như máy chủ). */
export function normalizePriceListData(grid: PriceGrid, data?: Partial<PriceListData> | null): PriceListData {
  const out = emptyPriceListData(grid)
  for (const { key } of ZONES) {
    const z = data?.[key] ?? (key === 'lien_tinh' ? data?.noi_mien : undefined)
    if (!z) continue
    out[key] = {
      prices: grid.breakpointsKg.map((_, i) => Math.max(0, Math.round(Number(z.prices?.[i] ?? 0)) || 0)),
      stepPrice: Math.max(0, Math.round(Number(z.stepPrice ?? 0)) || 0),
    }
  }
  return out
}

/** Hàng cân của bảng — chỉ phần gửi máy chủ. */
export function rowsPayload(grid: PriceGrid) {
  return { breakpointsKg: grid.breakpointsKg, kinds: grid.breakpointsKg.map((_, i) => kindAt(grid, i)), stepKg: grid.stepKg, maxWeightKg: grid.maxWeightKg }
}

export function sameRows(a: PriceGrid, b: PriceGrid): boolean {
  return JSON.stringify(rowsPayload(a)) === JSON.stringify(rowsPayload(b))
}

/** Lỗi hàng cân — cùng luật với máy chủ (validateGrid). */
export function gridIssues(grid: PriceGrid): string[] {
  const out: string[] = []
  const bp = grid.breakpointsKg.map(toGram)
  if (!bp.length) out.push('Bảng phải có ít nhất một hàng cân')
  bp.forEach((g, i) => {
    const k = kindAt(grid, i)
    if (!(g > 0)) out.push(`Hàng ${i + 1}: nhập số kg lớn hơn 0`)
    if (i > 0 && g > 0) {
      const pk = kindAt(grid, i - 1)
      if (g < bp[i - 1]! || (g === bp[i - 1]! && !(pk === 'lt' && k === 'eq'))) out.push(`Hàng ${i + 1} (${rowLabel(grid, i)}) phải nặng hơn hàng trước`)
    }
    if (k === 'eq' && g > 0 && !(i > 0 && kindAt(grid, i - 1) === 'lt' && bp[i - 1] === g)) {
      out.push(`Hàng "đúng ${fmtKg(g / 1000)} kg" phải đứng ngay sau hàng "dưới ${fmtKg(g / 1000)} kg"`)
    }
  })
  if (bp.length && kindAt(grid, bp.length - 1) === 'lt') out.push('Hàng cuối phải là "Đúng" hoặc "Đến" X kg')
  if (!(Math.round(grid.stepKg * 1000) >= 100)) out.push('Số kg mỗi bước tiếp theo tối thiểu 0,1kg')
  const last = grid.breakpointsKg[grid.breakpointsKg.length - 1]
  if (!(grid.maxWeightKg > 0)) out.push('Cân tối đa phải lớn hơn 0')
  else if (last !== undefined && grid.maxWeightKg < last) out.push('Cân tối đa không được nhỏ hơn hàng cuối')
  return out
}

/** Giá một kiện tại `gram` (null nếu vượt cân tối đa / không tra được). Khớp priceFor ở máy chủ. */
export function priceAtGram(grid: PriceGrid, data: PriceListData, zone: ZoneKey, gram: number): number | null {
  if (!(gram > 0) || gram > toGram(grid.maxWeightKg)) return null
  const z = data[zone]
  if (!z) return null
  const idx = grid.breakpointsKg.findIndex((kg, i) => {
    const g = toGram(kg)
    const k = kindAt(grid, i)
    return k === 'lt' ? gram < g : k === 'eq' ? gram === g : gram <= g
  })
  if (idx >= 0) return z.prices[idx] ?? null
  const last = toGram(grid.breakpointsKg[grid.breakpointsKg.length - 1] ?? 0)
  return (z.prices[z.prices.length - 1] ?? 0) + Math.ceil((gram - last) / toGram(grid.stepKg)) * z.stepPrice
}

/** Cân đại diện của hàng i (một cân nằm trong hàng). */
const repGram = (grid: PriceGrid, i: number) => toGram(grid.breakpointsKg[i]!) - (kindAt(grid, i) === 'lt' ? 1 : 0)

/** Lỗi nhập liệu đọc được: ô trống, giá giảm khi cân tăng. */
export function priceListIssues(grid: PriceGrid, data: PriceListData): string[] {
  const issues: string[] = []
  let empty = 0
  for (const { key, label } of ZONES) {
    const z = data[key]
    z.prices.forEach((p, i) => {
      if (!(p > 0)) empty++
      const prev = z.prices[i - 1]
      if (i > 0 && prev !== undefined && p > 0 && p < prev) {
        issues.push(`${label} ${rowLabel(grid, i)}: giá thấp hơn hàng nhẹ hơn (${money(prev)})`)
      }
    })
    if (hasStepZone(grid) && !(z.stepPrice > 0)) empty++
  }
  if (empty > 0) issues.unshift(`Còn ${empty} ô chưa điền giá`)
  return issues
}

/**
 * Các ô `value` thấp hơn `ref`. Cùng hàng → so từng ô; khác hàng (`refGrid`) → so giá `ref` tại
 * cân đại diện của từng hàng (gợi ý trên màn hình — máy chủ kiểm chính xác bằng khung ghép).
 */
export function cellsBelow(
  grid: PriceGrid,
  value: PriceListData,
  ref: PriceListData,
  refGrid?: PriceGrid | null,
): Array<CellRef & { value: number; ref: number }> {
  const out: Array<CellRef & { value: number; ref: number }> = []
  const same = !refGrid || sameRows(grid, refGrid)
  for (const { key } of ZONES) {
    grid.breakpointsKg.forEach((_, i) => {
      const v = value[key]?.prices[i] ?? 0
      const r = same ? (ref[key]?.prices[i] ?? 0) : priceAtGram(refGrid!, ref, key, repGram(grid, i))
      if (r !== null && v < r) out.push({ zone: key, cell: i, value: v, ref: r })
    })
    const vs = value[key]?.stepPrice ?? 0
    const rs = same ? (ref[key]?.stepPrice ?? 0) : null
    if (rs !== null && vs < rs) out.push({ zone: key, cell: 'step', value: vs, ref: rs })
  }
  return out
}

// ─── Sửa hàng ────────────────────────────────────────────────────

const KIND_ORDER: Record<RowKind, number> = { lt: 0, eq: 1, le: 2 }

/** Xếp hàng theo cân (cùng cân: dưới → đúng → đến), giá đi theo hàng. */
function sortRows(grid: PriceGrid, data: PriceListData): { grid: PriceGrid; data: PriceListData } {
  const idx = grid.breakpointsKg.map((_, i) => i)
  idx.sort((a, b) => grid.breakpointsKg[a]! - grid.breakpointsKg[b]! || KIND_ORDER[kindAt(grid, a)] - KIND_ORDER[kindAt(grid, b)] || a - b)
  if (idx.every((v, i) => v === i)) return { grid, data }
  const next: PriceGrid = { ...grid, breakpointsKg: idx.map((i) => grid.breakpointsKg[i]!), kinds: idx.map((i) => kindAt(grid, i)) }
  const d = {} as PriceListData
  for (const { key } of ZONES) d[key] = { ...data[key], prices: idx.map((i) => data[key].prices[i] ?? 0) }
  return { grid: next, data: d }
}

function KgInput({ value, onCommit, label, className }: { value: number; onCommit: (kg: number) => void; label: string; className?: string }) {
  const [text, setText] = useState(value > 0 ? fmtKg(value) : '')
  useEffect(() => setText(value > 0 ? fmtKg(value) : ''), [value])
  return (
    <input
      inputMode="decimal"
      value={text}
      aria-label={label}
      onChange={(e) => setText(e.target.value.replace(/[^\d.,]/g, ''))}
      onBlur={() => {
        const n = parseKg(text)
        onCommit(Number.isFinite(n) && n > 0 ? Math.round(n * 1000) / 1000 : 0)
      }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      className={cn(
        'h-10 desktop:h-9 w-16 rounded-md border border-rule bg-raise px-2 text-right text-sm tabular-nums text-ink focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20',
        className,
      )}
    />
  )
}

export interface PriceListEditorProps {
  grid: PriceGrid
  value: PriceListData
  onChange?: (next: PriceListData) => void
  /** Có thì sửa được hàng (thêm / xoá / đổi loại / đổi kg, bước và cân tối đa). */
  onRowsChange?: (grid: PriceGrid, data: PriceListData) => void
  readOnly?: boolean
  /** Bảng tham chiếu: ô nào `value` thấp hơn sẽ tô đỏ. */
  compareTo?: PriceListData | null
  /** Hàng của bảng tham chiếu (khác hàng của bảng đang sửa). */
  compareGrid?: PriceGrid | null
  compareLabel?: string
  /** Ô cần chú ý (vd. ô tăng giá do máy chủ trả về) — viền vàng. */
  flagged?: CellRef[]
  className?: string
}

export function PriceListEditor({
  grid,
  value,
  onChange,
  onRowsChange,
  readOnly = false,
  compareTo,
  compareGrid,
  compareLabel = 'Giá tham chiếu',
  flagged,
  className,
}: PriceListEditorProps) {
  const data = normalizePriceListData(grid, value)
  const below = compareTo ? cellsBelow(grid, data, normalizePriceListData(compareGrid ?? grid, compareTo), compareGrid) : []
  const belowOf = new Map(below.map((b) => [`${b.zone}:${b.cell}`, b.ref]))
  const flaggedSet = new Set((flagged ?? []).map((c) => `${c.zone}:${c.cell}`))
  const editRows = !readOnly && !!onRowsChange
  const showStep = editRows || hasStepZone(grid)
  const mobile = useViewportMode() === 'mobile'

  function setCell(zone: ZoneKey, cell: number | 'step', n: number) {
    if (!onChange) return
    const v = Math.max(0, Math.round(n || 0))
    const z = data[zone]
    onChange({
      ...data,
      [zone]: cell === 'step' ? { ...z, stepPrice: v } : { ...z, prices: z.prices.map((p, j) => (j === cell ? v : p)) },
    })
  }

  const rowsChange = (g: PriceGrid, d: PriceListData, sort = false) => {
    if (!onRowsChange) return
    const r = sort ? sortRows(g, d) : { grid: g, data: d }
    onRowsChange(r.grid, r.data)
  }
  const kindsOf = () => grid.breakpointsKg.map((_, i) => kindAt(grid, i))

  function setRow(i: number, patch: { kg?: number; kind?: RowKind }) {
    const kinds = kindsOf()
    const bps = [...grid.breakpointsKg]
    if (patch.kg !== undefined) bps[i] = patch.kg
    if (patch.kind) kinds[i] = patch.kind
    rowsChange({ ...grid, breakpointsKg: bps, kinds }, data, patch.kg !== undefined)
  }

  function addRow() {
    const n = grid.breakpointsKg.length
    const last = grid.breakpointsKg[n - 1] ?? 0
    const kg = last > 0 ? Math.floor(last) + 1 : 1
    const d = {} as PriceListData
    for (const { key } of ZONES) d[key] = { ...data[key], prices: [...data[key].prices, data[key].prices[n - 1] ?? 0] }
    rowsChange({ ...grid, breakpointsKg: [...grid.breakpointsKg, kg], kinds: [...kindsOf(), 'le'], maxWeightKg: Math.max(grid.maxWeightKg, kg) }, d)
  }

  function removeRow(i: number) {
    const d = {} as PriceListData
    for (const { key } of ZONES) d[key] = { ...data[key], prices: data[key].prices.filter((_, j) => j !== i) }
    rowsChange({ ...grid, breakpointsKg: grid.breakpointsKg.filter((_, j) => j !== i), kinds: kindsOf().filter((_, j) => j !== i) }, d)
  }

  function renderCell(zone: ZoneKey, zoneLabel: string, cell: number | 'step', label: string) {
    const v = cell === 'step' ? data[zone].stepPrice : (data[zone].prices[cell] ?? 0)
    const r = belowOf.get(`${zone}:${cell}`) ?? null
    const isBelow = r !== null
    const prev = cell !== 'step' && cell > 0 ? data[zone].prices[cell - 1] : undefined
    const decreasing = prev !== undefined && v > 0 && v < prev
    const isFlagged = flaggedSet.has(`${zone}:${cell}`)
    const title = [`${zoneLabel} · ${label}`, r !== null ? `${compareLabel}: ${money(r)}` : null, decreasing ? 'Thấp hơn giá hàng nhẹ hơn' : null]
      .filter(Boolean)
      .join('\n')

    return (
      <>
        {readOnly ? (
          <div
            title={title}
            className={cn('rounded px-1.5 py-1 text-right tabular-nums text-ink', isBelow && 'font-semibold text-danger', isFlagged && 'bg-warn/10 ring-1 ring-warn/40')}
          >
            {v > 0 ? money(v) : <span className="text-muted">—</span>}
          </div>
        ) : (
          <MoneyInput
            value={v}
            onValueChange={(n) => setCell(zone, cell, n)}
            aria-label={`${zoneLabel} ${label}`}
            aria-invalid={isBelow || undefined}
            title={title}
            className={cn('h-10 desktop:h-9 min-w-[108px]', (decreasing || isFlagged) && !isBelow && 'border-warn ring-2 ring-warn/20')}
          />
        )}
        {isBelow && <p className="mt-0.5 text-right text-xs text-danger tabular-nums">{compareLabel}: {money(r)}</p>}
      </>
    )
  }

  const renderTd = (zone: ZoneKey, zoneLabel: string, cell: number | 'step', label: string) => (
    <td key={zone} className="px-2 py-1.5 align-top">
      {renderCell(zone, zoneLabel, cell, label)}
    </td>
  )

  const kindSelect = (i: number) => (
    <select
      value={kindAt(grid, i)}
      onChange={(e) => setRow(i, { kind: e.target.value as RowKind })}
      aria-label={`Hàng ${i + 1}: loại`}
      className="h-10 desktop:h-9 rounded-md border border-rule bg-raise px-1.5 text-sm text-ink focus-visible:border-accent focus-visible:outline-none"
    >
      {(['lt', 'eq', 'le'] as const).map((k) => (
        <option key={k} value={k}>
          {ROW_KIND_LABEL[k]}
        </option>
      ))}
    </select>
  )

  const removeButton = (i: number, label: string) => (
    <button
      type="button"
      onClick={() => removeRow(i)}
      disabled={grid.breakpointsKg.length <= 1}
      aria-label={`Xóa hàng ${label}`}
      className="grid size-10 desktop:size-9 shrink-0 place-items-center rounded-md text-muted hover:bg-danger/10 hover:text-danger disabled:opacity-30"
    >
      <X className="size-4" />
    </button>
  )

  const addRowButton = editRows && (
    <button type="button" onClick={addRow} className="inline-flex h-11 desktop:h-9 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-accent hover:bg-accent/10">
      <Plus className="size-4" /> Thêm hàng
    </button>
  )

  // Điện thoại: mỗi mức cân là một thẻ, 4 vùng xếp 2 × 2 — bảng 4 cột không vừa bề ngang.
  if (mobile) {
    const zoneGrid = (cell: number | 'step', label: string) => (
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-2.5">
        {ZONES.map((z) => (
          <div key={z.key} className="min-w-0">
            <div className="mb-1 text-xs text-inkSoft">{z.label}</div>
            {renderCell(z.key, z.label, cell, label)}
          </div>
        ))}
      </div>
    )
    return (
      <div className={cn('space-y-2', className)}>
        {grid.breakpointsKg.map((kg, i) => {
          const label = rowLabel(grid, i)
          return (
            <div key={i} className="rounded-lg border border-rule bg-raise p-3">
              {editRows ? (
                <>
                  <div className="flex items-center gap-1.5 text-sm">
                    {kindSelect(i)}
                    <KgInput value={kg} onCommit={(n) => setRow(i, { kg: n })} label={`Hàng ${i + 1}: số kg`} />
                    <span className="text-inkSoft">kg</span>
                    <span className="ml-auto">{removeButton(i, label)}</span>
                  </div>
                  {kg > 0 && <div className="mt-0.5 text-xs text-muted">{rowRange(grid, i)}</div>}
                </>
              ) : (
                <div className="text-sm font-semibold text-ink">
                  {label}
                  {kindAt(grid, i) !== 'le' || grid.kinds ? <span className="block text-xs font-normal text-muted">{rowRange(grid, i)}</span> : null}
                </div>
              )}
              {zoneGrid(i, label)}
            </div>
          )
        })}
        {showStep && (
          <div className="rounded-lg border border-rule bg-paperSoft/60 p-3">
            {editRows ? (
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 text-sm text-ink">
                Mỗi
                <KgInput value={grid.stepKg} onCommit={(n) => rowsChange({ ...grid, stepKg: n }, data)} label="Số kg mỗi bước tiếp theo" className="w-14" />
                kg tiếp theo, tối đa
                <KgInput value={grid.maxWeightKg} onCommit={(n) => rowsChange({ ...grid, maxWeightKg: n }, data)} label="Cân tối đa (kg)" className="w-14" />
                kg
              </div>
            ) : (
              <div className="text-sm font-semibold text-ink">{stepRowLabel(grid)}</div>
            )}
            {zoneGrid('step', `mỗi ${fmtKg(grid.stepKg)} kg tiếp theo`)}
          </div>
        )}
        {addRowButton}
      </div>
    )
  }

  return (
    <div className={cn('rounded-lg border border-rule bg-raise', className)}>
      <div className="overflow-x-auto">
        <table className={cn('w-full border-collapse text-sm', editRows ? 'min-w-[740px]' : 'min-w-[580px]')}>
          <thead>
            <tr className="border-b border-rule bg-paperSoft text-xs text-inkSoft">
              <th className="sticky left-0 z-10 bg-paperSoft px-3 py-2 text-left font-semibold">Cân nặng</th>
              {ZONES.map((z) => (
                <th key={z.key} className="px-2 py-2 text-right font-semibold">
                  {z.label}
                </th>
              ))}
              {editRows && <th className="w-10 px-1 py-2"><span className="sr-only">Xóa hàng</span></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {grid.breakpointsKg.map((kg, i) => {
              const label = rowLabel(grid, i)
              return (
                <tr key={i}>
                  <th scope="row" className="sticky left-0 z-10 whitespace-nowrap bg-raise px-3 py-1.5 text-left align-top text-xs font-medium text-ink">
                    {editRows ? (
                      <span className="block">
                        <span className="flex items-center gap-1.5">
                          {kindSelect(i)}
                          <KgInput value={kg} onCommit={(n) => setRow(i, { kg: n })} label={`Hàng ${i + 1}: số kg`} />
                          <span className="text-inkSoft">kg</span>
                        </span>
                        {kg > 0 && <span className="mt-0.5 block text-2xs font-normal text-muted">{rowRange(grid, i)}</span>}
                      </span>
                    ) : (
                      <span className="block">
                        {label}
                        {kindAt(grid, i) !== 'le' || grid.kinds ? <span className="block text-2xs font-normal text-muted">{rowRange(grid, i)}</span> : null}
                      </span>
                    )}
                  </th>
                  {ZONES.map((z) => renderTd(z.key, z.label, i, label))}
                  {editRows && <td className="px-1 py-1.5 align-top">{removeButton(i, label)}</td>}
                </tr>
              )
            })}
            {showStep && (
              <tr className="bg-paperSoft/60">
                <th scope="row" className="sticky left-0 z-10 bg-paperSoft px-3 py-1.5 text-left align-top text-xs font-medium text-ink">
                  {editRows ? (
                    <span className="block space-y-1">
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        Mỗi
                        <KgInput value={grid.stepKg} onCommit={(n) => rowsChange({ ...grid, stepKg: n }, data)} label="Số kg mỗi bước tiếp theo" className="w-14" />
                        kg tiếp theo
                      </span>
                      <span className="flex items-center gap-1.5 whitespace-nowrap font-normal text-inkSoft">
                        tối đa
                        <KgInput value={grid.maxWeightKg} onCommit={(n) => rowsChange({ ...grid, maxWeightKg: n }, data)} label="Cân tối đa (kg)" className="w-14" />
                        kg
                      </span>
                    </span>
                  ) : (
                    <span className="block max-w-[150px] leading-snug">{stepRowLabel(grid)}</span>
                  )}
                </th>
                {ZONES.map((z) => renderTd(z.key, z.label, 'step', `mỗi ${fmtKg(grid.stepKg)} kg tiếp theo`))}
                {editRows && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editRows && <div className="flex flex-wrap items-center justify-between gap-2 border-t border-rule px-3 py-2">{addRowButton}</div>}
    </div>
  )
}

export default PriceListEditor
